import React, { useEffect, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/fonts/fonts.css";
import PubSub from "pubsub-js";
import { theme, muiTheme } from "./themes/themes";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
  ApolloLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import LayoutHome from "./containers/LayoutHome/LayoutHome";
import { cookie, startRefreshTokenTimer } from "./util/JWTCookie";
import { NotificationContainer } from "react-notifications";
import withAuthProvider from "./AuthProvider";
import LandingPageContainer from "./containers/LandingPageContainer";
import { ai } from "./helpers/appInsights";
import LoginPageContainer from "./containers/LoginPageContainer";
import ICEButtonContainer from "./containers/ICEButtonContainer/ICEButtonContainer";
import PropTypes from "prop-types";
import { LoadingProvider } from "./context/LoadingContext";
import { GlobalLoader } from "./components/ModalLoader/GlobalLoader";

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, location, path }) => {
      return `GraphQL Error ${message} at ${location} PATH: ${path}`;
    });
  }
});

const link = from([
  errorLink,
  new HttpLink({
    uri: process.env.APOLLO_CLIENT_URI,
  }),
]);

const linkPolicyInsight = from([
  errorLink,
  new HttpLink({
    uri: process.env.APOLLO_CLIENT_URI_POLICY_INSIGHT,
  }),
]);

const linkUsers = from([
  errorLink,
  new HttpLink({
    uri: process.env.APOLLO_CLIENT_URI_USERS,
    options: {
      reconnect: true,
    },
  }),
]);

const linkWellness = from([
  errorLink,
  new HttpLink({
    uri: process.env.APOLLO_CLIENT_URI_WELLNESS,
    options: {
      reconnect: true,
    },
  }),
]);

export const client = new ApolloClient({
  link: ApolloLink.split(
    (operation) => {
      return operation.getContext().clientName === "LinkUsers";
    },
    linkUsers, // <= apollo will send to this if clientName is "LinkUsers"
    link // <= otherwise will send to this
  ),
  cache: new InMemoryCache({
    addTypename: false,
  }),
  fetchOptions: {
    mode: "no-cors",
  },
});

export const clientWellness = new ApolloClient({
  // link: link,
  link: linkWellness,
  cache: new InMemoryCache({
    addTypename: false,
  }),
  fetchOptions: {
    mode: "no-cors",
  },
});

export const clientPolicyIngsight = new ApolloClient({
  // link: link,
  link: linkPolicyInsight,
  cache: new InMemoryCache({
    addTypename: false,
  }),
  fetchOptions: {
    mode: "no-cors",
  },
});

const RoutesAuth = ({ component, ...options }) => {
  if (options.path === "/ICE") {
    return <Route {...options} component={component}></Route>;
  }
  let user = cookie.get("TrustSafety");
  if (user?.userId) {
    startRefreshTokenTimer(user.exp);
    if (options.path === "/home") {
      return <Route {...options} component={component}></Route>;
    }
    return <Redirect to="/Home"></Redirect>;
  } else {
    if (options.path === "/" || options.path === "/login")
      return <Route {...options} component={component}></Route>;
    else return <Redirect to="/"></Redirect>;
  }
};

function App({ isAuthenticated, user, handleLogin, handleLogOut, getAccessToken }) {
  const [openSurveyCreation, setOpenSurveyCreation] = useState(false);
  const [idSurvey, setIdSurvey] = useState(0);
  const [isIntegrated, setIsIntegrated] = useState(false);
  const [isEditSurvey, setIsEditSurvey] = useState(false);
  const [dataEditSurvey, setDataEditSurvey] = useState();

  useEffect(() => {
    ai.appInsights.trackEvent({ name: "TnS Platform" });
  }, []);

  useEffect(() => {
    PubSub.subscribe("openSurveyCreation", (e, data) => {
      if (openSurveyCreation !== data) {
        setOpenSurveyCreation(data);
      }
    });
  }, [openSurveyCreation, idSurvey]);

  useEffect(() => {
    PubSub.subscribe("surveyId", (e, data) => {
      if (idSurvey !== data) {
        setIdSurvey(data);
      }
    });
  }, [idSurvey]);

  useEffect(() => {
    PubSub.subscribe("isIntegrated", (e, data) => {
      if (isIntegrated !== data) {
        setIsIntegrated(data);
      }
    });
  }, [isIntegrated]);

  useEffect(() => {
    PubSub.subscribe("isEditSurvey", (e, data) => {
      if (isEditSurvey !== data) {
        setIsEditSurvey(data);
      }
    });
  }, [isEditSurvey]);

  useEffect(() => {
    PubSub.subscribe("dataEditSurvey", (e, data) => {
      if (dataEditSurvey !== data) {
        setDataEditSurvey(data);
      }
    });
  }, [dataEditSurvey]);

  let landingPage = () => {
    return (
      <LandingPageContainer
        isAuthenticated={isAuthenticated}
        userInfo={user}
        handleLogin={handleLogin}
        handleLogOut={handleLogOut}
      />
    );
  };

  let loginPage = () => {
    return (
      <LoginPageContainer
        isAuthenticated={isAuthenticated}
        userInfo={user}
        handleLogin={handleLogin}
      />
    );
  };

  let layout = () => {
    return (
      <LayoutHome
        getAccessToken={getAccessToken}
        accessToken={user}
        logoutActDir={handleLogOut}
        isAuthenticated={isAuthenticated}
        user={user}
        handleLogin={handleLogin}
      />
    );
  };

  let ICEButton = () => {
    return <ICEButtonContainer />;
  };

  return (
    <LoadingProvider>
    <ApolloProvider client={client}>
      <MuiThemeProvider theme={muiTheme}>
        <StyledThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorBoundary>
            <div className="App">
              <BrowserRouter>
                <Switch>
                  <RoutesAuth exact path="/" component={landingPage}></RoutesAuth>
                  <RoutesAuth exact path="/login" component={loginPage} />
                  <RoutesAuth exact path="/home" component={layout}></RoutesAuth>
                  <RoutesAuth exact path="/ICE" component={ICEButton}></RoutesAuth>
                  <RoutesAuth
                    exact
                    path="*"
                    component={() => (
                      <LayoutHome
                        getAccessToken={getAccessToken}
                        accessToken={user}
                        logoutActDir={handleLogOut}
                        isAuthenticated={isAuthenticated}
                        user={user}
                        authButtonMethod={handleLogin}
                      ></LayoutHome>
                    )}
                  ></RoutesAuth>
                </Switch>
              </BrowserRouter>
            </div>
            <NotificationContainer />
            <GlobalLoader />
          </ErrorBoundary>
        </StyledThemeProvider>
      </MuiThemeProvider>
    </ApolloProvider>
    </LoadingProvider>
  );
}

export default withAuthProvider(App);
App.propTypes = {
  isAuthenticated: PropTypes.bool, 
  user: PropTypes.object, 
  handleLogin: PropTypes.func, 
  handleLogOut: PropTypes.func, 
  getAccessToken: PropTypes.func,
}
